<!-- LAYOUT: login.vue -->

<script setup lang="ts">
//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Vuetify
import { useDisplay } from "vuetify";

// Components
import AppBar from "../components/pages/login/organisms/AppBar.vue";
import LoginFooter from "../components/pages/login/organisms/LoginFooter.vue";
import { useTermsDialog } from "../composables/ui/dialogs/useTermsDialog";
import { useLoginPage } from "@/composables/pages/login/useLoginPage";

//
// ============================================================================
// COMPOSABLES
// ============================================================================
//

// Localization
const { t } = useI18n();

// Breakpoints
const { smAndDown } = useDisplay();

// Login Page
const { isCheckingAuth, agreeToTerms, laboratoryLogoUrl, labName, initialize } = useLoginPage();

// Get composables for locale-based refresh
const { locale } = useI18n();
const branding = useLaboratoryBranding();
const termsDialog = useTermsDialog("loginPage.terms");

//
// ============================================================================
// HEAD CONFIGURATION
// ============================================================================
//

// Update page title reactively
useHead(() => {
  const defaultName = t("pages.login.loginHeader.defaultLabName");
  const titleName = labName.value || defaultName;

  return {
    title: `${t("pages.login.loginForm.title")} - ${titleName}`,
  };
});

// Update favicon in DOM when logo URL changes (manual DOM update needed for blob URLs)
watch(laboratoryLogoUrl, (logoUrl) => {
  if (import.meta.client && logoUrl) {
    const existingLinks = document.querySelectorAll("link[rel='icon']");
    existingLinks.forEach((link) => link.remove());

    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/png";
    link.href = logoUrl;
    document.head.appendChild(link);
  }
});

//
// ============================================================================
// LIFECYCLE HOOKS
// ============================================================================
//

// Initialize lab data ONCE on layout mount
onMounted(async () => {
  await initialize();
});

// Watch for locale changes and refresh laboratory data
// Single watcher at layout level - prevents duplicate API calls
watch(locale, async (newLocale) => {
  await Promise.all([branding.refreshName(newLocale), termsDialog.loadTerms()]);
});
</script>

<template>
  <!-- Login Page's App Bar -->
  <app-bar
    v-show="!isCheckingAuth"
    :logo-src="laboratoryLogoUrl"
    :logo-alt="labName || t('pages.login.loginHeader.defaultLogoAlt')"
    :logo-size="36"
    :title="labName || t('pages.login.loginHeader.defaultLabName')"
    :subtitle="t('pages.login.loginHeader.labSubtitle')"
    :show-logo-text="!smAndDown"
    :agree-to-terms="agreeToTerms"
  />

  <v-main>
    <slot />
  </v-main>

  <!-- Login Page's Footer  -->
  <login-footer
    v-show="!isCheckingAuth"
    :logo-src="laboratoryLogoUrl"
    :logo-alt="labName || t('pages.login.loginHeader.defaultLogoAlt')"
    :title="labName || t('pages.login.loginHeader.defaultLabName')"
    :subtitle="t('pages.login.loginHeader.labSubtitle')"
    :description="t('pages.login.loginFooter.description')"
    :disclaimer="t('pages.login.loginFooter.disclaimer')"
    :description-links="[
      {
        keyword: 'LIMS',
        url: t('pages.login.loginFooter.skywareGroupUrl'),
        tooltip: t('pages.login.loginFooter.limsILabTooltip'),
      },
    ]"
  />
</template>
